// Pipeline progress view shown while a note is being created/processed —
// web equivalent of mobile's NoteCreateScreen (same STATUS_STEP mapping and
// create_step_* strings).
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import css from './note.module.css';
import type {Note} from '../../types/Note';
import {Icon} from '../common/Icon';
import {Button, Spinner} from '../common/ui';
import {
  subscribeUpload,
  retryUpload,
  type UploadState,
} from '../../services/createNote';
import {retryNote} from '../../services/functionsService';
import {useUpgrade} from '../../context/UpgradeProvider';
import {useToast} from '../../context/ToastProvider';
import owlLoading from '../../assets/owl_loading.png';

const STATUS_STEP: Record<string, number> = {
  uploading: 2,
  queued: 3,
  transcribing: 3,
  summarizing: 4,
  ready: 5,
};

const LIMIT_CODES = new Set([
  'FREE_LIMIT_REACHED',
  'AI_BUDGET_EXCEEDED',
  'VOICE_MESSAGE_LIMIT_REACHED',
]);

export function NoteProgress({note}: {note: Note}) {
  const {t} = useTranslation();
  const {showUpgrade} = useUpgrade();
  const showToast = useToast();
  const [upload, setUpload] = useState<UploadState | null>(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => subscribeUpload(note.id, setUpload), [note.id]);

  const limitError = note.error && LIMIT_CODES.has(note.error.code);
  const failed = note.status === 'error' || !!upload?.error;
  const step = STATUS_STEP[note.status] ?? 1;

  useEffect(() => {
    if (limitError) {
      showUpgrade(note.error!.code as never);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limitError]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      if (upload?.error) {
        retryUpload(note.id);
      } else if (note.error?.retryable !== false) {
        await retryNote(note.id);
      }
    } catch (e) {
      console.warn(e);
      showToast(t('upload_error'), 'error');
    } finally {
      setRetrying(false);
    }
  };

  if (failed) {
    return (
      <div className={css.progressWrap}>
        <div className={css.progressHero}>
          <img src={owlLoading} alt="" style={{filter: 'grayscale(0.7)'}} />
        </div>
        <h2 className={css.progressTitle}>{t('Error')}</h2>
        <p className={css.progressSub}>
          {upload?.error
            ? t('upload_error')
            : (note.error?.message ?? t('upload_error'))}
        </p>
        {limitError ? (
          <Button
            variant="primary"
            icon="crown"
            onClick={() => showUpgrade(note.error!.code as never)}
          >
            {t('upgrade_premium')}
          </Button>
        ) : (
          <Button
            variant="primary"
            icon="refresh"
            onClick={handleRetry}
            loading={retrying}
          >
            {t('TryAgain')}
          </Button>
        )}
      </div>
    );
  }

  const uploadPct = upload?.uploading ? upload.progress : null;

  return (
    <div className={css.progressWrap}>
      <div className={css.progressHero}>
        <img src={owlLoading} alt="" />
      </div>
      <h2 className={css.progressTitle}>{t('TranscribingYourMessage')}</h2>
      <p className={css.progressSub}>{t('SitTightWeWorking')}</p>
      <div className={css.progressBarOuter}>
        <div
          className={css.progressBarInner}
          style={{width: `${(step / 5) * 100}%`}}
        />
      </div>
      <div className={css.steps}>
        {/* Row 1 = check file, 2 = upload, 3 = transcribe, 4 = summarize,
            5 = done (mobile's create_step_* strings, minus the local-save
            step that doesn't exist on web). */}
        {['create_step_0', 'create_step_2', 'create_step_3', 'create_step_4', 'create_step_5'].map((key, i) => {
          const index = i + 1;
          const label = t(key);
          const stepClass =
            index < step
              ? css.stepDone
              : index === step
                ? css.stepActive
                : css.step;
          return (
            <div key={key} className={stepClass}>
              {index < step ? (
                <span className={css.stepIconDone}>
                  <Icon name="check" size={13} />
                </span>
              ) : index === step ? (
                <Spinner />
              ) : (
                <span className={css.stepIcon} />
              )}
              {label}
              {index === 2 && uploadPct !== null && ` (${uploadPct}%)`}
            </div>
          );
        })}
      </div>
      {upload?.uploading && (
        <p className={css.progressSub} style={{fontSize: 13}}>
          {t('WebKeepTabOpen')}
        </p>
      )}
      {!upload?.uploading && (
        <p className={css.progressSub} style={{fontSize: 13}}>
          {t('YouCanHide')}
        </p>
      )}
    </div>
  );
}
